import React, { useEffect, useState } from 'react';
import { Platform, View } from 'react-native';
import {
  Button,
  Modal,
  Portal,
  ToggleButton,
  useTheme,
} from 'react-native-paper';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
} from 'victory-native';
import PropTypes from 'prop-types';

const containerStyle = {
  backgroundColor: '#F2F2F2',
  paddingBottom: 50,
  height: Platform.OS !== 'web' ? '80%' : null,
};

const sharedAxisStyles = {
  tickLabels: {
    fontSize: 11,
  },
  axisLabel: {
    fontSize: 11,
  },
};

function WaterUseDataChart({
  appStyles,
  chartTitles,
  data,
  irrigation,
  language,
  toggleVisible,
  visible,
  unit,
}) {
  const [status, setStatus] = useState(irrigation ? 'surface' : 'monthly');
  const [layout, setLayout] = useState(undefined);

  const { colors } = useTheme();

  const hideModal = () => toggleVisible(false);

  useEffect(() => {
    if (irrigation) {
      setStatus('surface');
    } else {
      setStatus('monthly');
    }
  }, [irrigation]);

  const shortenName = (cropName) => {
    const nameParts = cropName.split('(');
    let shortenedName = '';

    if (nameParts.length > 0) {
      if (language === 'en') {
        if (nameParts[1] === 'Feb.-Apr. planting)') {
          shortenedName = '1st ' + nameParts[0];
        } else if (nameParts[1] === 'Aug. planting)') {
          shortenedName = '2nd ' + nameParts[0];
        } else if (
          nameParts[0].trim() === 'Corn' &&
          nameParts[1] === 'Aug.-Sept. planting)'
        ) {
          shortenedName = '1st ' + nameParts[0];
        } else if (nameParts[1] === 'Dec.-Jan. planting)') {
          shortenedName = '2nd ' + nameParts[0];
        } else if (nameParts[1] === 'Feb.-Mar. planting)') {
          shortenedName = '1st ' + nameParts[0];
        } else if (nameParts[1] === 'Aug.-Sept. planting)') {
          shortenedName = '2nd ' + nameParts[0];
        } else if (nameParts[1] === 'Mar.-Apr. planting)') {
          shortenedName = '1st ' + nameParts[0];
        } else {
          shortenedName = cropName;
        }
      } else {
        if (nameParts[1] === 'siembra en feb-abr)') {
          shortenedName = '1r ' + nameParts[0];
        } else if (nameParts[1] === 'siembra en ago)') {
          shortenedName = '2do ' + nameParts[0];
        } else if (
          nameParts[0].trim() === 'Maiz' &&
          nameParts[1] === 'siembra en ago-sep)'
        ) {
          shortenedName = '1r  ' + nameParts[0];
        } else if (nameParts[1] === 'siembra en dic-ene)') {
          shortenedName = '2do ' + nameParts[0];
        } else if (nameParts[1] === 'siembra en feb-mar)') {
          shortenedName = '1r ' + nameParts[0];
        } else if (nameParts[1] === 'siembra en ago-sep)') {
          shortenedName = '2do ' + nameParts[0];
        } else if (nameParts[1] === 'siembra en mar-abr)') {
          shortenedName = '1r ' + nameParts[0];
        } else {
          shortenedName = cropName;
        }
      }
    } else {
      shortenedName = cropName;
    }

    return shortenedName;
  };

  const checkStatus = (chartType) => {
    return chartType === status ? 'checked' : 'unchecked';
  };

  const getBarColor = () => {
    if (status === 'monthly') {
      return '#DDB945';
    } else if (status === 'surface') {
      return colors.accent;
    } else if (status === 'drip') {
      return '#80a13e';
    } else {
      return colors.primary;
    }
  };

  const getChartTitle = () => {
    let chartTitle = '';

    if (status === 'growing' || status === 'surface') {
      chartTitle = chartTitles[0].split('-')[1].trim();
    } else if (status === 'monthly' || status === 'sprinkler') {
      chartTitle = chartTitles[1].split('-')[1].trim();
    } else if (status === 'drip' && chartTitles.length > 2) {
      chartTitle = chartTitles[2].split('-')[1].trim();
    }

    return chartTitle;
  };

  const getValue = (record) => {
    let value = null;

    if (irrigation) {
      const percent =
        status === 'surface'
          ? 0.6
          : status === 'sprinkler'
          ? 0.75
          : status === 'drip'
          ? 0.9
          : 1;
      value = parseInt(record[unit]['monthlyWaterNeeds']) / percent;
    } else {
      value = parseInt(
        record[unit][
          status === 'monthly' ? 'monthlyWaterNeeds' : 'growingWaterNeeds'
        ]
      );
    }

    return value;
  };

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={hideModal}
        contentContainerStyle={containerStyle}
      >
        <View
          style={{ height: Platform.OS === 'web' ? 600 : null }}
          onLayout={(event) =>
            setLayout(Platform.OS !== 'web' ? event.nativeEvent.layout : null)
          }
        >
          {layout !== undefined ? (
            <VictoryChart
              style={{
                background: { fill: '#F2F2F2' },
                parent: { backgroundColor: '#F2F2F2' },
              }}
              height={500}
              domainPadding={{ x: 25, y: [0, 25] }}
              padding={{ top: 50, bottom: 75, left: 50, right: 50 }}
            >
              <VictoryAxis dependentAxis style={sharedAxisStyles} />
              <VictoryAxis
                style={sharedAxisStyles}
                // tickFormat={(t) =>
                //   t.length < 13 ? t.slice(0, 13) : t.slice(0, 10) + '...'
                // }
                tickLabelComponent={
                  <VictoryLabel
                    angle={-45}
                    textAnchor="end"
                    style={{ fontSize: 8 }}
                  />
                }
              />
              <VictoryBar
                x="crop"
                y="value"
                style={{
                  data: { fill: getBarColor() },
                  labels: { fontSize: 8, fontWeight: 'bold' },
                }}
                data={data.map((record) => ({
                  crop: shortenName(record.name),
                  value: getValue(record),
                }))}
                labels={({ datum }) => datum.value.toFixed(0)}
              />
              <VictoryLabel
                x={200}
                y={25}
                textAnchor="middle"
                text={getChartTitle()}
              />
            </VictoryChart>
          ) : null}
          <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
            <ToggleButton
              style={{ marginLeft: 5, marginRight: 5 }}
              icon="chart-bar"
              value={irrigation ? 'surface' : 'monthly'}
              status={checkStatus(irrigation ? 'surface' : 'monthly')}
              onPress={() => setStatus(irrigation ? 'surface' : 'monthly')}
            />
            <ToggleButton
              style={{ marginLeft: 5, marginRight: 5 }}
              icon="chart-bar"
              value={irrigation ? 'sprinkler' : 'growing'}
              status={checkStatus(irrigation ? 'sprinkler' : 'growing')}
              onPress={() => setStatus(irrigation ? 'sprinkler' : 'growing')}
            />
            {irrigation ? (
              <ToggleButton
                style={{ marginLeft: 5, marginRight: 5 }}
                icon="chart-bar"
                value="drip"
                status={checkStatus('drip')}
                onPress={() => setStatus('drip')}
              />
            ) : null}
          </View>
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'center',
              marginTop: 15,
            }}
          >
            <Button
              style={appStyles.button}
              labelStyle={appStyles.labelStyle}
              color={'#F06694'}
              icon="close"
              mode="contained"
              onPress={() => hideModal()}
            >
              {language === 'en' ? 'Close' : 'Cerrar'}
            </Button>
          </View>
        </View>
      </Modal>
    </Portal>
  );
}

WaterUseDataChart.propTypes = {
  chartTitles: PropTypes.arrayOf(PropTypes.string),
  data: PropTypes.array,
  irrigation: PropTypes.bool,
  language: PropTypes.string,
  toggleVisible: PropTypes.func,
  visible: PropTypes.bool,
  unit: PropTypes.string,
};

export default WaterUseDataChart;
